import { HoverableLinkSizes } from '@paywithextend/components'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { FormEvent, useState } from 'react'
import { useSelector } from 'react-redux'
import AppName from 'src/components/AppName/AppName'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import { useUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { signinUrl } from 'src/navigation/urls'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'
import { useNormalizeBackendError } from 'src/utils/error.utils'
import { normalizeEmail } from 'src/utils/string.utils'

import style from './SignupSsoForm.style'

export type SignupSsoFormProps = {
  className?: string
  inviteeEmail: string
  orgName: string
  inviterName: string
  inviterEmail: string
}

export default function SignupSsoForm(props: SignupSsoFormProps) {
  const {
    className = '',
    inviteeEmail,
    orgName,
    inviterName,
    inviterEmail,
  } = props

  const [loading, setLoading] = useState<boolean>(false)
  const [topLevelError, setTopLevelError] = useState<string | undefined>()

  const userContext = useUser()
  const translate = useTranslate()
  const brandCta = useSelector(selectBrandColorCta)
  const normalizeBackendError = useNormalizeBackendError()

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setTopLevelError(undefined)
    setLoading(true)

    try {
      const authConfig = await userContext.getAuthConfigFromEmail(
        normalizeEmail(inviteeEmail),
      )
      if (authConfig.authType === 'sso' && authConfig.identityProviderName) {
        await userContext.initiateSsoRedirect(authConfig.identityProviderName)
        return
      }
      // Unexpected: authConfig came back as password on the continue click
      // but we already detected SSO on page load. Reset loading and show error.
      setLoading(false)
      setTopLevelError(
        translate(
          'Your organization requires single sign-on for your account, and we were unable to start this process. Please contact your administrator or try again later.',
        ),
      )
    } catch (error) {
      setLoading(false)

      const defaultErrorMessage = translate(
        'Sorry, something went wrong. Please try again later.',
      )

      const axiosError = error as {
        response?: { data?: { message?: string } }
      }
      if (axiosError?.response?.data?.message) {
        setTopLevelError(axiosError.response.data.message)
        return
      }

      const normalized = normalizeBackendError(error)
      setTopLevelError(normalized.error || defaultErrorMessage)
    }
  }

  return (
    <div className={cn('SignupSsoForm', className)} css={style}>
      <form onSubmit={handleSubmit}>
        <span className="heading">
          {translate('Sign up for ${appName} and join ${orgName}', {
            appName: <AppName />,
            orgName: <span className="orgName">{orgName}</span>,
          })}
        </span>
        <p className="description">
          {translate(
            '${inviterNameAndEmail} has invited you to join their organization in ${appName}. If you already have an account, you can accept or decline after you ${logInLink}.',
            {
              inviterNameAndEmail: (
                <span className="inviterNameAndEmail">
                  {`${inviterName} (`}
                  <HoverableLink
                    size={HoverableLinkSizes.Large}
                    href={`mailto:${inviterEmail}`}
                    underline
                    ariaLabelSource={inviterEmail}
                  >
                    {inviterEmail}
                  </HoverableLink>
                  {`)`}
                </span>
              ),
              appName: <AppName />,
              logInLink: (
                <HoverableLink
                  size={HoverableLinkSizes.Large}
                  to={signinUrl()}
                  underline
                  ariaLabelSource={translate('log in')}
                >
                  {translate('log in')}
                </HoverableLink>
              ),
            },
          )}
        </p>
        <TextInput
          label={translate('Email')}
          name="email"
          value={inviteeEmail}
          disabled
          iconRight={<SvgLocked className="lockedIcon" fill={brandCta} />}
        />
        {topLevelError && <span className="errorText">{topLevelError}</span>}
        <Button
          className="submitButton"
          type="submit"
          loading={loading}
          testId="sso-continue-button"
        >
          {translate('Continue')}
        </Button>
        <span className="footer">
          {translate('Already with ${appName}? ${logIn}', {
            logIn: (
              <HoverableLink
                size={HoverableLinkSizes.Small}
                to={signinUrl()}
                underline
                ariaLabelSource={translate('Log in')}
              >
                {translate('Log in')}
              </HoverableLink>
            ),
            appName: <AppName />,
          })}
        </span>
      </form>
    </div>
  )
}
